import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../../helper/api.interceptor";
import AddUserModal from "../AddUserModal";
import Swal from "sweetalert2";

const OnsiteManager = () => {
    const [onsiteManagers, setOnsiteManagers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [showAddModal, setShowAddModal] = useState(false);

    const changeRef = useRef(Math.random());

    // Fetch Onsite Manager List
    const fetchOnsiteManagers = async () => {
        try {
            const data = await api.getAlOnsiteExecutive(); // correct API
            console.log("Onsite Managers:", data);

            const sorted = (data || []).sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );

            setOnsiteManagers(sorted);
        } catch (error) {
            console.error("Error fetching onsite managers:", error);
        }
    };

    useEffect(() => {
        fetchOnsiteManagers();
    }, []);

    // Filter Logic
    const filteredManagers = onsiteManagers.filter((mgr) => {
        const q = searchQuery.trim().toLowerCase();
        const statusMatch =
            filterStatus === "All" || (mgr?.documents?.status || "") === filterStatus;

        return statusMatch && (mgr?.username || "").toLowerCase().includes(q);
    });

    // Pagination
    const totalPages = Math.ceil(filteredManagers.length / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = currentPage * itemsPerPage;
    const currentManagers = filteredManagers.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [filteredManagers, totalPages]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);

    // Delete Onsite Manager
    const handleDeleteClick = async (phone_number) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This will remove Onsite Executive role from this user.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove role!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const payload = {
                    phone_number,
                    role: "Onsite Executive"
                };

                const response = await api.removeUserRole(payload);

                if (response?.status_code === 200) {
                    Swal.fire("Removed!", "Role removed successfully.", "success");
                    fetchOnsiteManagers();
                } else {
                    Swal.fire("Error!", response?.message || "Failed to remove role.", "error");
                }
            } catch (error) {
                Swal.fire("Error!", error.message, "error");
            }
        }
    };

    return (
        <div>
            {/* Header + Breadcrumb */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">
                    <Icon
                        icon="mdi:map-marker-path" className="menu-icon"
                        style={{ width: "30px", height: "30px" }}
                    />{" "}
                    | Onsite Managers
                </h6>

                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link
                            to="/"
                            className="d-flex align-items-center gap-1 hover-text-primary"
                        >
                            <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                            Role Management
                        </Link>
                    </li>
                    <li>-</li>
                    <li className="fw-medium">Onsite Managers</li>
                </ul>
            </div>

            {/* Card */}
            <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    {/* Show Count */}
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value={filteredManagers.length}>All</option>
                        </select>
                    </div>

                    {/* Search + Add */}
                    <div className="d-flex align-items-center gap-3">
                        <div className="icon-field">
                            <input
                                type="text"
                                className="form-control form-control-sm w-auto"
                                placeholder="Search by Name"
                                onChange={handleSearchChange}
                            />
                            <span className="icon">
                                <Icon icon="ion:search-outline" />
                            </span>
                        </div>

                        <button
                            onClick={() => setShowAddModal(true)}
                            className="btn btn-warning rounded-pill fw-medium d-flex align-items-center gap-2"
                        >
                            <Icon icon="mdi:plus" className="fs-5" />
                            Add New Onsite Manager
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="card-body" style={{ overflowX: "auto" }}>
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th>S.L</th>
                                <th>Onsite Executive Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Warehouse</th>
                                <th>Pincode</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {currentManagers.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center">
                                        No Onsite Manager found.
                                    </td>
                                </tr>
                            ) : (
                                currentManagers.map((mgr, index) => (
                                    <tr key={mgr._id}>
                                        <td>{indexOfFirstItem + index + 1}</td>
                                        <td>{mgr?.username || "No Name"}</td>
                                        <td>{mgr?.emailid || "No Email"}</td>
                                        <td>{mgr?.phone_number || "No Phone"}</td>
                                        <td>{mgr?.warehouse_name || "N/A"}</td>
                                        <td>{mgr?.address?.pincode || "N/A"}</td>

                                        <td>
                                            <Link
                                                onClick={() => handleDeleteClick(mgr.phone_number)}
                                                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24">
                        <span>
                            Showing {indexOfFirstItem + 1} to{" "}
                            {Math.min(indexOfLastItem, filteredManagers.length)} of{" "}
                            {filteredManagers.length} entries
                        </span>

                        <nav>
                            <ul className="pagination">
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li
                                        key={i}
                                        className={`page-item ${currentPage === i + 1 ? "active" : ""
                                            }`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <AddUserModal
                    show={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    onUserAdded={fetchOnsiteManagers}
                />
            )}
        </div>
    );
};

export default OnsiteManager;
